'use client';

import * as React from 'react';
import { cfLogoUrl } from '@/lib/image';

interface TenantLogoProps {
  /** Light-mode logo URL. Callers render their own fallback when this is empty. */
  light: string;
  /** Dark-mode logo URL; used under `prefers-color-scheme: dark` when present. */
  dark?: string | null;
  alt: string;
  /** Intrinsic width in CSS px (sets the <img> width; src is requested at 2x). */
  width: number;
  /** Intrinsic height in CSS px. */
  height: number;
  /** Classes applied to the rendered <img> — sizing / object-fit live here. */
  className?: string;
  /** Hide the element if the image fails to load (matches header.tsx behavior). */
  hideOnError?: boolean;
  /** Rendered in the same footprint when the selected light/dark image fails. */
  fallback?: React.ReactNode;
}

/**
 * Tenant/company logo with a system dark-mode swap.
 *
 * The app's theme is `prefers-color-scheme` (Tailwind `darkMode: 'media'`), so a
 * `<picture>` with a `(prefers-color-scheme: dark)` `<source>` is the right tool:
 * the browser picks the correct variant with no JS, no hydration step, and a
 * single fetch — and it's SSR/cache-safe (no class toggling that could mismatch
 * statically-cached HTML). When a tenant has no dark variant, the light logo is
 * used in both modes (unchanged behavior). URLs route through `cfLogoUrl` for
 * resize + format-auto.
 *
 * `display: contents` on the `<picture>` keeps the `<img>` laid out as if it were
 * the caller's direct child, so existing sizing classes (`w-full h-full`,
 * `h-9 w-auto`, `size-5`, …) behave exactly as before.
 */
export function TenantLogo({
  light,
  dark,
  alt,
  width,
  height,
  className,
  hideOnError,
  fallback,
}: TenantLogoProps) {
  const req = { width: width * 2, height: height * 2 };
  const lightSrc = cfLogoUrl(light, req);
  const darkSrc = dark ? cfLogoUrl(dark, req) : null;
  const sourceKey = `${lightSrc}\n${darkSrc ?? ''}`;
  const [failedSource, setFailedSource] = React.useState<string | null>(null);

  if (failedSource === sourceKey && fallback != null) return <>{fallback}</>;

  return (
    <picture className="contents">
      {darkSrc ? <source srcSet={darkSrc} media="(prefers-color-scheme: dark)" /> : null}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={lightSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        {...(hideOnError || fallback != null
          ? {
              onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
                if (fallback != null) setFailedSource(sourceKey);
                else e.currentTarget.style.display = 'none';
              },
            }
          : {})}
      />
    </picture>
  );
}
